import React from "react"
import {useTranslation} from "react-i18next"

// Components
import {EcgMeasurement} from "../ModernCaliperLayer"

// Utils
import {acceptsParameter, ParameterKey} from "../paramLimits"

// Styles
import useStyles from "./styles"

/**
 * Where a *time* caliper reading can legitimately be written.
 *
 * Amplitude readings are deliberately absent: every editable field in
 * `commonParameters` is a duration or an axis in degrees, so there is nowhere
 * to put a millivolt. The design prototype offers the axis fields for amplitude
 * drags, which would write mV into a degrees column — this bar reports those
 * measurements and stops there.
 */
const TIME_TARGETS: {key: ParameterKey, label: string}[] = [
    {key: "heartRate", label: "HR"},
    {key: "prInterval", label: "PR"},
    {key: "qrsDuration", label: "QRS"},
    {key: "qtInterval", label: "QT"},
    {key: "pDuration", label: "P dur"},
    {key: "tDuration", label: "T dur"},
];

type ModernMeasureBarProps = {
    measurement: EcgMeasurement
    /** False on the preview route, for company masters and approved records. */
    editable: boolean
    onApply(key: ParameterKey, value: number): void
    onClose(): void
}

/**
 * Floating readout for the last caliper drag, with a one-step "write this into
 * a parameter" action. Ported from the prototype's `.measurebar`.
 *
 * @param props
 */
export default function ModernMeasureBar(props: ModernMeasureBarProps) {
    const styles = useStyles();
    const [t] = useTranslation("common");
    const {measurement, editable, onApply, onClose} = props;

    const readout = measurement.mode === "time"
        ? `${measurement.ms.toFixed(0)} ms · ${measurement.bpm.toFixed(0)} bpm`
        : `${measurement.mv.toFixed(2)} mV · ${measurement.mm.toFixed(1)} mm`;

    /**
     *
     * @param key
     */
    function valueFor(key: ParameterKey): number {
        if (measurement.mode !== "time") {
            return NaN
        }

        return Math.round(key === "heartRate" ? measurement.bpm : measurement.ms)
    }

    function handleApply(event: React.ChangeEvent<HTMLSelectElement>) {
        const key = event.target.value as ParameterKey;

        if (!key || measurement.mode !== "time") {
            return
        }

        onApply(key, valueFor(key))
    }

    return (
        <div className={styles.bar}>
            <span className={styles.value}>{readout}</span>

            {measurement.mode === "time" && editable && (
                <select
                    className={styles.apply}
                    defaultValue=""
                    aria-label={t("modern.apply_to_parameter")}
                    onChange={handleApply}
                >
                    <option value="">{t("modern.apply_to_parameter")}</option>
                    {TIME_TARGETS.map(target => {
                        const fits = acceptsParameter(target.key, valueFor(target.key));

                        return (
                            <option key={target.key} value={target.key} disabled={!fits}>
                                {fits ? target.label : `${target.label} — ${t("modern.out_of_range_short")}`}
                            </option>
                        )
                    })}
                </select>
            )}

            {measurement.mode === "amp" && (
                <span className={styles.note}>{t("modern.amplitude_readonly")}</span>
            )}

            <button
                type="button"
                className={styles.close}
                onClick={onClose}
                title={t("modern.clear_measurement")}
                aria-label={t("modern.clear_measurement")}
            >
                ×
            </button>
        </div>
    )
}
